import { useState } from 'react'

// Calendar export utility
// Generates .ics files for post-search relationship maintenance

function formatICSDate(date) {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

function createICSEvent({ uid, summary, description, dtstart, rrule }) {
  return `BEGIN:VEVENT
UID:${uid}
SUMMARY:${summary}
DESCRIPTION:${description}
DTSTART;VALUE=DATE:${dtstart}
DTEND;VALUE=DATE:${dtstart}
RRULE:${rrule}
BEGIN:VALARM
TRIGGER:-P1D
ACTION:DISPLAY
DESCRIPTION:${summary}
END:VALARM
END:VEVENT`
}

function downloadICS(filename, content) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function generateCalendarPack(hotTen, innerCircle) {
  const events = []
  const today = new Date()
  const startDate = today.toISOString().split('T')[0].replace(/-/g, '')

  // Hot Ten — quarterly check-ins
  hotTen.forEach((contact, i) => {
    if (!contact.name) return
    events.push(createICSEvent({
      uid: `hot-ten-${contact.id || i}-quarterly@applicationmultiplier`,
      summary: `Check in with ${contact.name}`,
      description: `A quick message, a response to something they posted, or a coffee if you're local. Relationships are maintained in small moments. ${contact.company ? `${contact.name} is at ${contact.company}.` : ''}`,
      dtstart: startDate,
      rrule: 'FREQ=YEARLY;INTERVAL=1;BYMONTH=1,4,7,10',
    }))
  })

  // Inner Circle — bi-annual touchpoints
  innerCircle.forEach((person, i) => {
    if (!person.name) return
    events.push(createICSEvent({
      uid: `inner-circle-${person.id || i}-biannual@applicationmultiplier`,
      summary: `Connect with ${person.name}`,
      description: `${person.name} was part of your Inner Circle during your job search. Don't let six months go by without a real conversation.`,
      dtstart: startDate,
      rrule: 'FREQ=YEARLY;INTERVAL=1;BYMONTH=1,7',
    }))
  })

  // Annual Job Spec refresh
  events.push(createICSEvent({
    uid: 'job-spec-annual@applicationmultiplier',
    summary: 'Update your Personal Job Spec',
    description: 'Even if you\'re happily employed, knowing what you\'d want next keeps you clear. Open The Application Multiplier app and review your Job Spec. Update your salary floor, your non-negotiables, what you\'d start and stop.',
    dtstart: `${new Date().getFullYear() + 1}0101`,
    rrule: 'FREQ=YEARLY;INTERVAL=1;BYMONTH=1;BYMONTHDAY=2',
  }))

  // Annual LinkedIn refresh
  events.push(createICSEvent({
    uid: 'linkedin-refresh-annual@applicationmultiplier',
    summary: 'Refresh your LinkedIn profile',
    description: 'Your LinkedIn is not just for job searching. Update your headline, your About section, and your recent accomplishments. Keep it current so recruiters can find you — even when you\'re not looking.',
    dtstart: `${new Date().getFullYear() + 1}0601`,
    rrule: 'FREQ=YEARLY;INTERVAL=1;BYMONTH=6;BYMONTHDAY=1',
  }))

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//The Application Multiplier//Relationship Maintenance//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:Application Multiplier - Stay Connected
X-WR-CALDESC:Reminders to maintain the relationships that got you hired.
${events.join('\n')}
END:VCALENDAR`

  downloadICS('ApplicationMultiplier_StayConnected.ics', icsContent)
}

export default function CalendarExport({ session, hotTen, innerCircle }) {
  const [downloading, setDownloading] = useState(false)
  const [downloaded, setDownloaded] = useState(false)

  async function handleDownload() {
    setDownloading(true)
    
    // Load Hot Ten and Inner Circle if not provided
    let contacts = hotTen
    let circle = innerCircle
    
    if (!contacts) {
      const { supabase } = await import('../supabase')
      const [h, i] = await Promise.all([
        supabase.from('hot_ten').select('*').eq('user_id', session.user.id),
        supabase.from('inner_circle').select('*').eq('user_id', session.user.id),
      ])
      contacts = h.data || []
      circle = i.data || []
    }

    generateCalendarPack(contacts, circle || [])
    setDownloading(false)
    setDownloaded(true)
  }

  // Need useState import
  const [, forceUpdate] = [downloading, setDownloading]

  return (
    <div style={{ background: '#E8F2EC', borderRadius: '10px', padding: '1.25rem', textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📅</div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.5rem' }}>
        Stay close to the people who helped you
      </div>
      <p style={{ color: '#555', fontSize: '0.82rem', lineHeight: 1.65, marginBottom: '1rem' }}>
        The relationships that got you hired will get you hired again. Download calendar reminders to check in with your Hot Ten quarterly and your Inner Circle twice a year.
      </p>
      <button
        onClick={handleDownload}
        disabled={downloading || downloaded}
        style={{ background: '#00712D', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: downloading || downloaded ? 'default' : 'pointer', opacity: downloading ? 0.7 : 1, width: '100%' }}
      >
        {downloading ? 'Generating...' : downloaded ? '✓ Calendar downloaded' : 'Download calendar reminders →'}
      </button>
      {downloaded && (
        <p style={{ fontSize: '0.75rem', color: '#00712D', marginTop: '0.5rem' }}>
          Open the .ics file to import into Google Calendar, Apple Calendar, or Outlook.
        </p>
      )}
    </div>
  )
}


